import {useCallback, useMemo, useState} from 'react'
import {ScrollView, View} from 'react-native'
import {Image} from 'expo-image'
import {plural} from '@lingui/core/macro'
import {Plural, useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {gradeLabel, subjectLabel} from '#/lib/curriculum'
import {type NavigationProp} from '#/lib/routes/types'
import {niceDate} from '#/lib/strings/time'
import {
  canPostInClassroom,
  type Classroom,
  libraryEntryVisibility,
  useClassroomActor,
} from '#/state/classrooms'
import {
  useClassroomPostViews,
  usePostViewsByUri,
} from '#/state/queries/classroom-posts'
import {
  useAddClassroomContentMutation,
  useClassroomContentQuery,
} from '#/state/queries/classrooms'
import {
  type ActorFlashbookRecord,
  useActorFlashbooksQuery,
} from '#/state/queries/flashbook'
import {useSession} from '#/state/session'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {Check_Stroke2_Corner0_Rounded as CheckIcon} from '#/components/icons/Check'
import {Loader} from '#/components/Loader'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {showClassroomError} from './showClassroomError'

/**
 * Putting a FlashBook into a classroom.
 *
 * A book is not written here. Sharing one means pointing the classroom at a book
 * the account has already published: nothing is copied and nothing is re-published,
 * so the room shows the same post, which is why a book in a classroom can be read,
 * commented on and reacted to like anything else. It is not a duplicate of the one
 * on the profile — it *is* that one.
 *
 * A book made *for* the room is the other half of the story and does not arrive
 * through here: it is written in the studio, from this room, and it is published as
 * a post like any other while saying on its records that it belongs to that room
 * (see `isClassroomExclusive`). This dialog is about the first kind — the ones
 * already published, and therefore already readable by anyone.
 *
 * It is a *library* rather than a list of buttons, and for the same reason the
 * capsule picker is: a teacher with a year of books has dozens, and what they have
 * is a question — the one from last year, for the fifth year, in Maths. So the picker
 * is a search and two filters over a grid of small cards, each card saying what the
 * book is, who it is for, when it was made, and how much of it is work.
 *
 * Posts are not written here at all. A post for a classroom is written in the
 * composer, and goes into the room by being posted.
 */
export function AddFlashbookDialog({
  control,
  classroom,
}: {
  control: DialogControlProps
  classroom: Classroom
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const actor = useClassroomActor()
  const navigation = useNavigation<NavigationProp>()
  const {hasSession} = useSession()
  const {gtMobile} = useBreakpoints()

  const canPost = canPostInClassroom(classroom, actor)
  const [selectedUri, setSelectedUri] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [levelFilter, setLevelFilter] = useState<string | null>(null)
  const [subjectFilter, setSubjectFilter] = useState<string | null>(null)
  const [oldestFirst, setOldestFirst] = useState(false)
  /** Bumped to clear the search box; see the same state in `AddCapsuleDialog`. */
  const [searchGeneration, setSearchGeneration] = useState(0)

  const {data: inRoomPages} = useClassroomContentQuery(
    classroom.id,
    'flashbook',
  )
  const inRoom = inRoomPages?.pages.flatMap(page => page.content) ?? []
  const addContent = useAddClassroomContentMutation(classroom.id)
  /**
   * The books this room already holds.
   *
   * A shared book is a pointer at a post, and a post is in a room once (see
   * `addContent`), so one that is already here can only be a card that does
   * nothing. It stays in the grid and says so rather than disappearing — a library
   * that quietly hides a third of itself is a library somebody will search twice for
   * the same missing book.
   *
   * Both addresses are read, not only the post's, because a book made *for* this room
   * carries the record's address and a post's that is indistinguishable from a public
   * one: matching on the post alone would let a teacher pick their own room's book
   * from the library and be told it was added.
   */
  const alreadyHere = useMemo(() => {
    const posts = new Set<string>()
    const books = new Set<string>()
    for (const entry of inRoom) {
      if (entry.postUri) posts.add(entry.postUri)
      if (entry.flashbookUri) books.add(entry.flashbookUri)
    }
    return {posts, books}
  }, [inRoom])

  const {data: records, isPending} = useActorFlashbooksQuery(
    canPost ? actor?.did : undefined,
  )

  /**
   * Everything this account has published, newest first.
   *
   * Two kinds of record are left out on purpose, and they are the same kind of thing
   * said two ways. A **draft** has no post, so there is no post to point this
   * classroom at, and it is not published at all — the studio's own unfinished list
   * is where it belongs. A book marked `visibility: 'classroom'` *is* a post now —
   * that is what lets the room it was written for read it and answer it — but it
   * belongs to that room, and a book made for one classroom is not a thing another
   * classroom can be sent.
   */
  const library = useMemo<FlashbookChoice[]>(() => {
    const rows: FlashbookChoice[] = []
    for (const record of records ?? []) {
      if (!record.postUri || record.status === 'draft') continue
      if (record.visibility === 'classroom') continue
      /* A room pinned to a year holds that year's work and no other (see the capsule picker). */
      if (classroom.grade && record.level !== classroom.grade) continue
      rows.push({
        visibility: record.visibility,
        flashbookUri: record.uri,
        postUri: record.postUri,
        title: record.title.trim() || firstLine(record.description),
        description: record.description,
        level: record.level,
        subject: record.subject,
        pageCount: record.pageCount,
        exerciseCount: record.exerciseCount,
        cover: record.cover,
        createdAt: record.createdAt,
      })
    }
    return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }, [classroom.grade, records])

  /**
   * Books whose post is not there any more.
   *
   * A record outlives its post: deleting the post leaves the book behind, pointing
   * at an address with nothing at it, and the library is built from records — so
   * without this the picker offers work that arrives in the room as *This book is no
   * longer here.*, which is a row nobody can read and nobody asked for. Only said
   * once the posts have actually answered: a post that has not come back *yet* is not
   * the same as one that is gone.
   */
  const postUris = useMemo(
    () => library.map(choice => choice.postUri),
    [library],
  )
  const {data: postViews} = useClassroomPostViews(postUris)
  const viewsByUri = usePostViewsByUri(postViews)
  const unavailable = useMemo(() => {
    const missing = new Set<string>()
    if (!postViews) return missing
    const here = new Set(postViews.map(post => post.uri))
    for (const choice of library) {
      if (!here.has(choice.postUri)) missing.add(choice.postUri)
    }
    return missing
  }, [library, postViews])

  /** Only the filters this library can answer with: no empty chip rows. */
  const levels = useMemo(
    () =>
      Array.from(
        new Set(
          library.map(choice => choice.level).filter((v): v is string => !!v),
        ),
      ).sort(),
    [library],
  )
  const subjects = useMemo(
    () =>
      Array.from(
        new Set(
          library.map(choice => choice.subject).filter((v): v is string => !!v),
        ),
      ).sort(),
    [library],
  )

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const filtered = library.filter(choice => {
      if (levelFilter && choice.level !== levelFilter) return false
      if (subjectFilter && choice.subject !== subjectFilter) return false
      if (
        needle &&
        !`${choice.title} ${choice.description}`.toLowerCase().includes(needle)
      ) {
        return false
      }
      return true
    })
    return oldestFirst ? [...filtered].reverse() : filtered
  }, [levelFilter, library, oldestFirst, query, subjectFilter])

  const clearFilters = useCallback(() => {
    setQuery('')
    setLevelFilter(null)
    setSubjectFilter(null)
    setSearchGeneration(generation => generation + 1)
  }, [])

  const selected = useMemo(
    () =>
      library.find(
        choice =>
          choice.flashbookUri === selectedUri || choice.postUri === selectedUri,
      ) ?? null,
    [library, selectedUri],
  )

  const onAdd = useCallback(() => {
    if (!actor || !selected) return
    addContent.mutate(
      {
        classroomId: classroom.id,
        kind: 'flashbook',
        // Published work is public in the room unless it is classroom-only.
        visibility: libraryEntryVisibility(selected),
        text: selected.description,
        flashbookTitle: selected.title,
        postUri: selected.postUri,
        flashbookUri: selected.flashbookUri,
      },
      {
        onSuccess: () => Toast.show(l`FlashBook added to ${classroom.name}`),
        onError: error => showClassroomError(i18n, error),
      },
    )
    setSelectedUri(null)
    control.close()
  }, [
    actor,
    addContent,
    classroom.id,
    classroom.name,
    control,
    i18n,
    l,
    selected,
  ])

  const onOpenStudio = useCallback(() => {
    setSelectedUri(null)
    control.close()
    // The classroom travels with the studio, so publishing there puts the book in
    // this room and comes back to it.
    navigation.navigate('FlashBookStudio', {classroomId: classroom.id})
  }, [classroom.id, control, navigation])

  if (!hasSession || !canPost) {
    return (
      <Dialog.Outer control={control}>
        <Dialog.Handle />
        <Dialog.ScrollableInner label={l`Add a FlashBook to this classroom`}>
          <View style={[a.gap_lg]}>
            <Text style={[a.text_xl, a.font_bold]}>
              {l`Adding is for whoever runs this classroom and its teachers`}
            </Text>
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              {l`The account that runs this classroom, and the teachers in it, put the work here. Students and parents read it.`}
            </Text>
          </View>
          <Dialog.Close />
        </Dialog.ScrollableInner>
      </Dialog.Outer>
    )
  }

  const loading = isPending && library.length === 0

  return (
    <Dialog.Outer control={control} onClose={() => setSelectedUri(null)}>
      <Dialog.Handle />
      <Dialog.ScrollableInner
        label={l`Add a FlashBook to ${classroom.name}`}
        style={[a.w_full, {maxWidth: 640}]}>
        <View style={[a.gap_md]}>
          <View style={[a.gap_xs]}>
            <Text style={[a.text_xl, a.font_bold]}>
              {l`Add a FlashBook to ${classroom.name}`}
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {l`Your published books. Adding one shares it with this classroom — it stays on your profile, and anyone can read it.`}
            </Text>
          </View>

          {loading ? (
            <View style={[a.flex_row, a.align_center, a.gap_sm, a.py_2xl]}>
              <Loader size="sm" />
              <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
                {l`Looking for your books…`}
              </Text>
            </View>
          ) : library.length === 0 ? (
            <NothingPublished onOpenStudio={onOpenStudio} />
          ) : (
            <>
              <Filters
                searchGeneration={searchGeneration}
                query={query}
                onQuery={setQuery}
                levels={levels}
                level={levelFilter}
                onLevel={setLevelFilter}
                subjects={subjects}
                subject={subjectFilter}
                onSubject={setSubjectFilter}
                oldestFirst={oldestFirst}
                onOldestFirst={setOldestFirst}
                shown={results.length}
                total={library.length}
              />

              {results.length === 0 ? (
                <View
                  testID="classroomFlashbookFilterEmpty"
                  style={[
                    a.gap_md,
                    a.p_lg,
                    a.rounded_md,
                    a.align_center,
                    t.atoms.bg_contrast_25,
                  ]}>
                  <Text style={[a.text_md, a.font_semi_bold, a.text_center]}>
                    {l`No book matches that.`}
                  </Text>
                  <Text
                    style={[
                      a.text_sm,
                      a.text_center,
                      t.atoms.text_contrast_medium,
                    ]}>
                    {l`Your library is still here — these filters are just narrower than it is.`}
                  </Text>
                  <Button
                    testID="classroomFlashbookClearFilters"
                    label={l`Clear the filters`}
                    color="secondary"
                    size="small"
                    onPress={clearFilters}>
                    <ButtonText>{l`Clear filters`}</ButtonText>
                  </Button>
                </View>
              ) : (
                <FlashbookGrid
                  choices={results}
                  columns={gtMobile ? 3 : 2}
                  selectedUri={selectedUri}
                  alreadyHere={alreadyHere}
                  unavailable={unavailable}
                  viewsByUri={viewsByUri}
                  onSelect={uri =>
                    setSelectedUri(current => (current === uri ? null : uri))
                  }
                />
              )}

              <View style={[a.gap_sm, a.pt_xs]}>
                <Button
                  testID="classroomFlashbookSubmit"
                  label={l`Add this FlashBook to the classroom`}
                  size="large"
                  color="primary"
                  disabled={!selected}
                  onPress={onAdd}>
                  <ButtonText>{l`Add to this classroom`}</ButtonText>
                </Button>
                <Button
                  testID="classroomMakeFlashbook"
                  label={l`Write a new FlashBook for this classroom`}
                  color="secondary"
                  size="small"
                  style={[a.self_start]}
                  onPress={onOpenStudio}>
                  <ButtonText>{l`Write a new FlashBook`}</ButtonText>
                </Button>
              </View>
            </>
          )}
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/** One book that can be shared into the classroom. */
interface FlashbookChoice {
  /** The record's own visibility (`classroom` for classroom-only work). */
  visibility?: string
  /** The book record — what tells this book from one with the same title. */
  flashbookUri: string
  /** The post it was published as — what the classroom will point at. */
  postUri: string
  title: string
  description: string
  /** The grade code the publisher chose (`Level_A5`), not a name. */
  level?: string
  subject?: string
  pageCount?: number
  exerciseCount?: number
  cover?: ActorFlashbookRecord['cover']
  createdAt: string
}

/**
 * The search box, the two filters and the order.
 *
 * Only what the library can answer with appears: a year nobody has published at is
 * not a chip, because a filter that can only ever empty the grid is a filter that
 * teaches nothing. The count beside the order is the other half of that — it says
 * how much of the library the current filters are showing, so a teacher who has
 * narrowed too far can see that they have.
 */
function Filters({
  searchGeneration,
  query,
  onQuery,
  levels,
  level,
  onLevel,
  subjects,
  subject,
  onSubject,
  oldestFirst,
  onOldestFirst,
  shown,
  total,
}: {
  /** Changes when the app clears the box; see the state that owns it. */
  searchGeneration: number
  query: string
  onQuery: (value: string) => void
  levels: string[]
  level: string | null
  onLevel: (value: string | null) => void
  subjects: string[]
  subject: string | null
  onSubject: (value: string | null) => void
  oldestFirst: boolean
  onOldestFirst: (value: boolean) => void
  shown: number
  total: number
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View testID="classroomFlashbookPicker" style={[a.gap_sm]}>
      <TextField.Root>
        <TextField.Input
          key={`classroomFlashbookSearch-${searchGeneration}`}
          testID="classroomFlashbookSearch"
          label={l`Search your books`}
          placeholder={l`Search your books`}
          defaultValue={query}
          onChangeText={onQuery}
        />
      </TextField.Root>

      {levels.length > 1 ? (
        <ChipRow
          label={l`Year`}
          allLabel={l`All years`}
          testIDPrefix="classroomFlashbookLevel"
          options={levels.map(value => ({
            value,
            // A record stores `Level_A5`; a teacher reads "5th year".
            label: l(gradeLabel(value) ?? {message: value}),
          }))}
          selected={level}
          onSelect={onLevel}
        />
      ) : null}

      {subjects.length > 1 ? (
        <ChipRow
          label={l`Subject`}
          allLabel={l`All subjects`}
          testIDPrefix="classroomFlashbookSubject"
          options={subjects.map(value => ({
            value,
            label: l(subjectLabel(value) ?? {message: value}),
          }))}
          selected={subject}
          onSelect={onSubject}
        />
      ) : null}

      <View style={[a.flex_row, a.align_center, a.gap_sm]}>
        <Text style={[a.flex_1, a.text_sm, t.atoms.text_contrast_medium]}>
          {shown === total
            ? plural(total, {one: '# book', other: '# books'})
            : l`${shown} of ${total} books`}
        </Text>
        <Button
          testID="classroomFlashbookSort"
          label={oldestFirst ? l`Oldest books first` : l`Newest books first`}
          color="secondary"
          size="tiny"
          onPress={() => onOldestFirst(!oldestFirst)}>
          <ButtonText>
            {oldestFirst ? l`Oldest first` : l`Newest first`}
          </ButtonText>
        </Button>
      </View>
    </View>
  )
}

/** One filter, as a strip of chips. See `AddCapsuleDialog` for why it scrolls. */
function ChipRow({
  label,
  allLabel,
  testIDPrefix,
  options,
  selected,
  onSelect,
}: {
  label: string
  allLabel: string
  testIDPrefix: string
  options: {value: string; label: string}[]
  selected: string | null
  onSelect: (value: string | null) => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View style={[a.gap_xs]}>
      <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text_contrast_medium]}>
        {label}
      </Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[a.flex_row, a.gap_xs]}>
        <Chip
          testID={`${testIDPrefix}-all`}
          label={allLabel}
          selected={!selected}
          onPress={() => onSelect(null)}
        />
        {options.map(option => (
          <Chip
            key={option.value}
            testID={`${testIDPrefix}-${option.value}`}
            label={option.label}
            selected={selected === option.value}
            onPress={() =>
              onSelect(selected === option.value ? null : option.value)
            }
          />
        ))}
      </ScrollView>
    </View>
  )
}

function Chip({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string
  selected: boolean
  onPress: () => void
  testID: string
}) {
  return (
    <Button
      testID={testID}
      label={label}
      size="tiny"
      color={selected ? 'primary' : 'secondary'}
      shape="default"
      onPress={onPress}>
      <ButtonText>{label}</ButtonText>
    </Button>
  )
}

/**
 * The library itself: small cards, as many as fit.
 *
 * The columns are decided here and the rows are laid out by hand, because a grid in
 * a scrolling dialog is not a grid in a list: `Grid.Row` is built to fill the height
 * of a list row it was handed, and inside a plain column that height is a question
 * nobody has answered. Flex columns keep every card the same width and an equal share
 * of the row, and the last row is padded so its single card is the size of a card and
 * not the width of the dialog.
 */
function FlashbookGrid({
  choices,
  columns,
  selectedUri,
  alreadyHere,
  unavailable,
  viewsByUri,
  onSelect,
}: {
  choices: FlashbookChoice[]
  columns: number
  selectedUri: string | null
  alreadyHere: {posts: Set<string>; books: Set<string>}
  /** Books whose post no longer exists; see the set's own note. */
  unavailable: Set<string>
  viewsByUri: Map<string, {embed?: unknown}>
  onSelect: (flashbookUri: string) => void
}) {
  const rows = useMemo(() => {
    const out: FlashbookChoice[][] = []
    for (let i = 0; i < choices.length; i += columns) {
      out.push(choices.slice(i, i + columns))
    }
    return out
  }, [choices, columns])

  return (
    <ScrollView
      testID="classroomFlashbookGrid"
      style={[{maxHeight: 340}]}
      contentContainerStyle={[a.gap_sm]}>
      {rows.map(row => (
        <View
          key={row.map(choice => choice.flashbookUri).join(' ')}
          style={[a.flex_row, a.gap_sm, a.align_stretch]}>
          {row.map(choice => (
            <View key={choice.flashbookUri} style={[a.flex_1]}>
              <FlashbookChoiceCard
                choice={choice}
                selected={selectedUri === choice.flashbookUri}
                alreadyHere={
                  alreadyHere.posts.has(choice.postUri) ||
                  alreadyHere.books.has(choice.flashbookUri)
                }
                unavailable={unavailable.has(choice.postUri)}
                viewsByUri={viewsByUri}
                onPress={() => onSelect(choice.flashbookUri)}
              />
            </View>
          ))}
          {Array.from({length: columns - row.length}).map((_, i) => (
            <View key={`filler-${i}`} style={[a.flex_1]} />
          ))}
        </View>
      ))}
    </ScrollView>
  )
}

/**
 * One book, as a card.
 *
 * The cover when the record has one and its year, its subject and the month it was
 * made underneath — which is the set of facts a teacher filters by, so it is the set
 * of facts the card has to carry. A book whose cover is a still is drawn with the
 * still; a book whose cover is a film is drawn with the film's own poster frame, which
 * the publisher also wrote onto the post (`coverImageUri`), so a library is pictures
 * rather than a wall of empty tiles. A book with neither gets the tile and the same
 * words, because a cover is a nicety and the title is not.
 */
function FlashbookChoiceCard({
  choice,
  selected,
  alreadyHere,
  unavailable,
  viewsByUri,
  onPress,
}: {
  choice: FlashbookChoice
  selected: boolean
  alreadyHere: boolean
  unavailable: boolean
  viewsByUri: Map<string, {embed?: unknown}>
  onPress: () => void
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  /** Nothing to add, for one of two reasons — and each has its own word. */
  const blocked = alreadyHere || unavailable

  const level = choice.level ? gradeLabel(choice.level) : null
  const subject = choice.subject ? subjectLabel(choice.subject) : null
  const meta = [
    level ? l(level) : null,
    subject ? l(subject) : null,
    choice.createdAt
      ? niceDate(i18n, choice.createdAt, 'medium', 'none')
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  /*
   * The cover: the record's own artwork, a film cover's derived still, or the
   * picture the publisher wrote onto the post. Three sources because three
   * generations of published books exist, and the order is the order they can be
   * reached in — the record is the one the reader plays, and the post's field is
   * the one that is always there for a book published since covers were.
   */
  const postRecord = viewsByUri.get(choice.postUri) as
    {record?: {coverImageUri?: string}} | undefined
  const cover =
    choice.cover?.imageUri ??
    choice.cover?.videoThumbnailUri ??
    postRecord?.record?.coverImageUri

  const pages =
    typeof choice.pageCount === 'number' && choice.pageCount > 0
      ? choice.pageCount
      : null

  return (
    <Button
      testID={`classroomFlashbookOption-${choice.flashbookUri}`}
      label={l`Add ${choice.title}`}
      disabled={blocked}
      onPress={onPress}
      style={[a.w_full, a.flex_1]}>
      {({hovered, pressed}) => (
        <View
          style={[
            a.flex_1,
            a.rounded_lg,
            a.border,
            {
              padding: 2,
              borderWidth: 2,
              borderColor: selected ? t.palette.primary_500 : 'transparent',
              opacity: blocked ? 0.55 : 1,
            },
          ]}>
          <View
            style={[
              a.flex_1,
              a.rounded_md,
              a.overflow_hidden,
              a.border,
              t.atoms.border_contrast_low,
              t.atoms.bg_contrast_25,
              (hovered || pressed) && !blocked
                ? t.atoms.border_contrast_high
                : null,
            ]}>
            <View style={[{aspectRatio: 4 / 5}]}>
              {cover ? (
                <Image
                  source={{uri: cover}}
                  style={[a.w_full, a.h_full]}
                  contentFit="cover"
                  accessibilityIgnoresInvertColors
                />
              ) : (
                /*
                 * A book with no cover art is not an empty tile: what the card has
                 * instead is the thing being chosen — the title — and an empty
                 * rectangle would make a third of a library look broken.
                 */
                <View
                  style={[
                    a.w_full,
                    a.h_full,
                    a.justify_center,
                    a.align_center,
                    a.p_sm,
                    t.atoms.bg_contrast_50,
                  ]}>
                  <Text
                    emoji
                    numberOfLines={3}
                    style={[
                      a.text_sm,
                      a.font_bold,
                      a.text_center,
                      t.atoms.text_contrast_high,
                    ]}>
                    {choice.title || l`Untitled book`}
                  </Text>
                </View>
              )}
              {pages ? (
                <View
                  style={[
                    a.absolute,
                    {
                      top: 6,
                      left: 6,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 3,
                      backgroundColor: 'rgba(0,0,0,0.55)',
                      borderRadius: 20,
                      paddingHorizontal: 8,
                      paddingVertical: 3,
                    },
                  ]}>
                  <Text style={[a.text_xs]}>📖</Text>
                  <Text
                    style={[
                      a.text_xs,
                      a.font_bold,
                      {color: 'white', lineHeight: 14},
                    ]}>
                    {/* A book of one page is not "1 pages". */}
                    <Plural value={pages} one="# page" other="# pages" />
                  </Text>
                </View>
              ) : null}
              {blocked ? (
                /*
                 * Said as a chip on the cover rather than across it. The card is
                 * already dimmed and does not respond, so the words only have to
                 * name the reason.
                 */
                <View
                  testID={
                    alreadyHere
                      ? `classroomFlashbookInRoom-${choice.flashbookUri}`
                      : `classroomFlashbookGonePick-${choice.flashbookUri}`
                  }
                  style={[a.absolute, {bottom: 6, left: 6}]}>
                  <View
                    style={[
                      a.rounded_full,
                      a.px_sm,
                      {
                        paddingVertical: 3,
                        backgroundColor: 'rgba(0,0,0,0.65)',
                      },
                    ]}>
                    <Text
                      style={[
                        a.text_xs,
                        a.font_semi_bold,
                        {color: 'white', lineHeight: 14},
                      ]}>
                      {alreadyHere
                        ? l`In this classroom`
                        : l`No longer available`}
                    </Text>
                  </View>
                </View>
              ) : selected ? (
                <View
                  style={[
                    a.absolute,
                    {
                      top: 6,
                      right: 6,
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor: t.palette.primary_500,
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  ]}>
                  <CheckIcon size="sm" fill="white" />
                </View>
              ) : null}
            </View>

            <View style={[a.p_xs, a.gap_2xs, {paddingBottom: 8}]}>
              <Text
                emoji
                numberOfLines={2}
                style={[a.text_sm, a.font_semi_bold, a.leading_tight]}>
                {choice.title || l`Untitled book`}
              </Text>
              <Text
                numberOfLines={2}
                style={[
                  a.text_2xs,
                  a.leading_tight,
                  t.atoms.text_contrast_medium,
                ]}>
                {meta || l`No year or subject`}
              </Text>
            </View>
          </View>
        </View>
      )}
    </Button>
  )
}

/**
 * An account with nothing published.
 *
 * Two situations wear the same words because they are the same problem from the
 * teacher's side — nothing here can be added — and the same way out: a book written
 * in the studio. If it is published from the studio while a classroom is open, it
 * lands in that classroom by itself; if not, it comes back here as a card to share.
 */
function NothingPublished({onOpenStudio}: {onOpenStudio: () => void}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View
      testID="classroomFlashbookPickerEmpty"
      style={[a.gap_md, a.p_lg, a.rounded_md, t.atoms.bg_contrast_25]}>
      <Text style={[a.text_md, a.font_semi_bold]}>
        {l`You have no published books to share yet`}
      </Text>
      <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
        {l`A FlashBook is written in the studio. Publishing it puts it on your profile, where anyone can read it and where this room can be pointed at it — and a book you publish from inside a classroom stays between you and that classroom instead.`}
      </Text>
      <Button
        testID="classroomOpenBookStudio"
        label={l`Open the book studio`}
        color="primary"
        size="small"
        style={[a.self_start]}
        onPress={onOpenStudio}>
        <ButtonText>{l`Open the studio`}</ButtonText>
      </Button>
    </View>
  )
}

/** The first line of a body of text, as a card's title. */
function firstLine(text: string): string {
  const [line] = text.trim().split('\n')
  return line?.trim() ?? ''
}
